import React from "react";
import { Row, Col, InputNumberProps, InputProps } from "antd";
import { ViewIcon, ViewOffSlashIcon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { useIsRequired } from "src/components/Form/requiredFields";

import {
  FormContainer,
  FormElement,
  Label,
  InputNumber,
  Wrapper,
  Text,
  Input,
  Action,
  ButtonContainer,
  TextArea,
  Password,
  FieldError,
  RequiredMark,
  ScreenReaderOnly,
} from "./styles/form";
import { PasswordStrength } from "src/components/ui";

import {
  lowercaseRegex,
  numberRegex,
  specialCharRegex,
  uppercaseRegex,
} from "src/utils/regex";
import { theme } from "src/theme/theme";

interface FormProps {
  total?: string | number;
  amount?: string | number;
  newTotal?: string | number;
  children?: React.ReactNode;
}

const Form = ({ total, amount, newTotal, children, ...rest }: FormProps) => {
  return (
    <FormContainer {...rest}>
      <Row>
        <Col span={24}>
          <Wrapper>{children}</Wrapper>
        </Col>
      </Row>
    </FormContainer>
  );
};

interface InvestInputNumberProps extends InputNumberProps {
  name?: string;
  label?: React.ReactNode;
  error?: string;
  /** Marks the label, so a field that must be filled in says so. */
  required?: boolean;
}

/**
 * A label, with the mark when the field is required.
 *
 * A form that only reveals which fields are mandatory when you try to submit
 * makes the reader guess; the asterisk is the convention for saying it up
 * front, and the screen-reader word says it to anyone the convention does not
 * reach.
 */
export const FieldLabel = ({
  children,
  required,
  name,
}: {
  children?: React.ReactNode;
  required?: boolean;
  /** Given instead of `required`, the form's schema decides. */
  name?: string;
}) => {
  /* The hook runs unconditionally and the prop wins over it. Written as
     required ?? useIsRequired(name) the call was SKIPPED whenever a caller
     passed the prop, so a field that supplies it on one render and
     not the next changes the hook order and React tears the component down.
     No caller does that today, which is why it has not bitten — it is a
     rule about what the code permits, not about what it currently does. */
  const fromSchema = useIsRequired(name);
  const isRequired = required ?? fromSchema;

  return (
    <>
      {children}
      {isRequired && (
        <>
          <RequiredMark aria-hidden="true">*</RequiredMark>
          <ScreenReaderOnly> required</ScreenReaderOnly>
        </>
      )}
    </>
  );
};

Form.InputNumber = function FormInputNumber({
  onChange,
  defaultValue,
  label,
  formatter,
  parser,
  name,
  error,
  id,
  color,
  required,
  ...rest
}: InvestInputNumberProps) {
  return (
    <>
      <Label htmlFor={id}>
        {/* The name stays ink whatever the state of the field: it names the
            field, it is not the complaint. */}
        <Text>
          <FieldLabel required={required}>{label}</FieldLabel>
        </Text>
      </Label>
      <InputNumber
        id={id}
        name={name}
        aria-required={required || undefined}
        defaultValue={defaultValue}
        size="large"
        formatter={formatter}
        parser={parser}
        onChange={onChange}
        color={color}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      {error && <FieldError role="alert">{error}</FieldError>}
    </>
  );
};

interface InputFormProps extends InputProps {
  name?: string;
  error?: string;
  label?: React.ReactNode;
  showRules?: boolean;
}

interface PasswordInputFormProps extends InputFormProps {
  value?: string;
}

Form.Input = function FormInput({
  label,
  id,
  error,
  onChange,
  ...rest
}: InputFormProps) {
  return (
    <>
      {/* The name stays ink whatever the state of the field — it names the
          field, it is not the complaint — and the complaint goes UNDER the
          field, in the one error style the product uses.

          Rendered only when there IS a name. The label is a flex box with a
          6px margin under it, so an empty one still took a line: a field
          composed inside a group that carries the name itself — the three
          boxes of a birth date under one "Birth date" — sat 21px lower than
          the plain fields beside it, and the row came out stepped. A field
          with no name of its own should not reserve room for one. */}
      {label ? (
        <Label htmlFor={id}>
          <Text>{label}</Text>
        </Label>
      ) : null}
      {/* The id goes ON the field as well as in the label's htmlFor. It was
          consumed here and never passed down, so every label in the app
          pointed at an element that did not exist: clicking a field's name
          focused nothing, and a screen reader read the box as unlabelled. */}
      <Input
        id={id}
        size="large"
        onChange={onChange}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      {error && <FieldError role="alert">{error}</FieldError>}
    </>
  );
};

Form.Password = function FormPassword({
  label,
  id,
  error,
  value = "",
  onChange,
  showRules,
  ...rest
}: PasswordInputFormProps) {
  return (
    <>
      <Label htmlFor={id}>
        <Text>{label}</Text>
      </Label>
      <Password
        /* The reveal toggle is muted, not accent. It sits INSIDE a field
           whose focus ring is already the accent, and an accent glyph in
           every password box competes with it for the one thing the accent
           is supposed to mean. Football's is the same: muted ink, darkening
           on hover. */
        iconRender={(visible) => (
          <HugeIcon icon={visible ? ViewIcon : ViewOffSlashIcon} />
        )}
        id={id}
        size="large"
        onChange={onChange}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={showRules ? `${id}-strength` : undefined}
        {...rest}
      />
      {error && <FieldError role="alert">{error}</FieldError>}
      {/* The meter and the checklist, from football's own component: visible
          from an empty field, muted until a rule is met and green once it is.
          The five rules used to live here as a wrapping row of 12px/300 spans
          that were invisible until you typed and then turned RED for
          everything not yet done. */}
      {showRules && <PasswordStrength id={`${id}-strength`} value={value} />}
    </>
  );
};

Form.Element = function InputFormElement({ children, ...rest }) {
  return <FormElement {...rest}>{children}</FormElement>;
};

Form.Action = function FormAction({ children, ...rest }) {
  return <Action {...rest}>{children}</Action>;
};

Form.ButtonContainer = function FormButton({ children, ...rest }) {
  return <ButtonContainer {...rest}>{children}</ButtonContainer>;
};

Form.TextArea = function FormTextArea({ label, id, error, onChange, ...rest }) {
  return (
    <>
      <Label htmlFor={id}>
        <Text>{label}</Text>
      </Label>
      <TextArea
        onChange={onChange}
        autoSize={{ minRows: 3, maxRows: 5 }}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      {/* It rendered no error at all: an invalid comment reddened the label
          above the field and said nothing about what was wrong. */}
      {error && <FieldError role="alert">{error}</FieldError>}
    </>
  );
};

interface LabelProps {
  label?: string;
  id?: string;
  error?: string;
  children?: React.ReactNode;
}

Form.Label = function FormLabel({ children, label, id, error }: LabelProps) {
  return (
    <>
      <Label htmlFor={id}>
        <Text>{label}</Text>
      </Label>
      {children}
      {error && <FieldError role="alert">{error}</FieldError>}
    </>
  );
};

export default Form;
